import { Reveal } from "../Reveal";
import { VISIT } from "../../lib/content";
import { MapPin, Mail, Phone, Instagram } from "lucide-react";

export default function Footer() {
  return (
    <footer
      id="visit"
      data-testid="footer-section"
      className="relative bg-ink text-bone overflow-hidden"
      style={{
        backgroundImage:
          "url('https://customer-assets.emergentagent.com/job_matcha-cafe-hub/artifacts/8zfnamhp_MATCHA%20STORY%20PATTERN.jpg')",
        backgroundSize: "520px",
        backgroundRepeat: "repeat",
        backgroundPosition: "center",
      }}
    >
      {/* Dark overlay for legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "rgba(20, 32, 22, 0.78)" }}
        aria-hidden
      />
      <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-12">
        {/* Visit headline */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <div className="flex items-center gap-4 mb-6">
                <span className="overline text-bone/70">— Final Chapter</span>
                <span className="h-px w-24 bg-bone/30" />
              </div>
              <h2
                className="font-serif font-light leading-[1] tracking-tight"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
              >
                Come in,
                <br />
                <span className="italic font-cormorant text-matcha">
                  the bowl is warm.
                </span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <Reveal delay={0.1}>
              <p className="text-bone/70 leading-relaxed">
                Find us whisking with passion and practice — the art of serving one of the best matcha drinks in town with 100% authentic Japanese matcha.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Visit details */}
        <div className="grid grid-cols-12 gap-8 md:gap-12 pb-20 md:pb-28 border-b border-bone/15">
          <Reveal className="col-span-12 md:col-span-4">
            <p className="overline text-bone/60 mb-6">Find Us</p>
            <p className="font-serif text-2xl leading-snug">
              {VISIT.address}
              <br />
              {VISIT.city}
            </p>
            <a
              data-testid="footer-directions"
              href={`https://maps.google.com/?q=${encodeURIComponent(
                VISIT.address + ", " + VISIT.city,
              )}`}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 overline text-matcha hover:text-bone transition-colors"
            >
              <MapPin size={14} /> Get directions
            </a>
          </Reveal>

          <Reveal delay={0.1} className="col-span-12 md:col-span-4">
            <p className="overline text-bone/60 mb-6">Hours</p>
            <ul className="space-y-3">
              {VISIT.hours.map((h) => (
                <li
                  key={h.day}
                  className="flex items-center justify-between font-cormorant text-xl border-b border-bone/10 pb-2"
                >
                  <span className="text-bone/85">{h.day}</span>
                  <span className="text-bone">{h.time}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2} className="col-span-12 md:col-span-4">
            <p className="overline text-bone/60 mb-6">Reach Us</p>
            <ul className="space-y-4 text-bone/85">
              <li>
                <a
                  data-testid="footer-email"
                  href={`mailto:${VISIT.email}`}
                  className="inline-flex items-center gap-3 hover:text-matcha transition-colors group"
                >
                  <Mail size={16} className="text-matcha" />
                  <span className="font-cormorant text-xl">{VISIT.email}</span>
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-phone"
                  href={`tel:${VISIT.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-3 hover:text-matcha transition-colors"
                >
                  <Phone size={16} className="text-matcha" />
                  <span className="font-cormorant text-xl">{VISIT.phone}</span>
                </a>
              </li>
              {VISIT.social.map((s) => (
                <li key={s.label}>
                  <a
                    data-testid={`footer-social-${s.label.toLowerCase()}`}
                    href={s.href}
                    className="inline-flex items-center gap-3 hover:text-matcha transition-colors"
                  >
                    <Instagram size={16} className="text-matcha" />
                    <span className="font-cormorant text-xl">{s.handle}</span>
                  </a>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Massive logotype */}
        <div className="pt-16 md:pt-24">
          <Reveal>
            <h3
              className="font-display leading-[1] text-bone text-center"
              style={{
                fontSize: "clamp(3rem, 14vw, 14rem)",
                letterSpacing: "0.08em",
              }}
            >
              Matcha
              <br />
              <span className="text-matcha">Story</span>
            </h3>
          </Reveal>

          <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-bone/55 text-xs">
            <p className="overline">
              © {new Date().getFullYear()} Matcha Story. All rights reserved.
            </p>
            <p className="font-cormorant italic text-base">
              Crafted matcha · Desserts · Good vibes
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
